/**
 * PacmanKanbanScreen - Member's turn after Pac-Man capture
 * Shows assigned issues with timer and Pac-Man styling
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useGame } from '../context/GameContext';
import { useFireworksCode } from '../hooks/useFireworksCode';
import { IssuesKanban } from '../components/IssuesKanban';
import { FireworksOverlay } from '../components/pacman';
import { getInitials, getAvatarColor, formatTime } from '../utils';
import type { Blocker } from '../types';
import './PacmanKanbanScreen.css';

const TIMER_DURATION = 120; // 2 minutes
const WARNING_THRESHOLD = 10;

export function PacmanKanbanScreen() {
  const { state, actions } = useGame();
  const { currentMember, remainingMembers, chouquetteMode, pacmanCaptureOrder } = state;

  const [timeRemaining, setTimeRemaining] = useState(TIMER_DURATION);
  const [isTimerRunning, setIsTimerRunning] = useState(true);
  const [showBlockerInput, setShowBlockerInput] = useState(false);
  const [blockerText, setBlockerText] = useState('');
  const [chouquetteTriggered, setChouquetteTriggered] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);

  const blockerInputRef = useRef<HTMLInputElement>(null);
  const isLastMember = remainingMembers.length === 0;

  // Timer effect
  useEffect(() => {
    if (!isTimerRunning || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          setIsTimerRunning(false);
          if (chouquetteMode && !chouquetteTriggered) {
            handleChouquetteDebt();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isTimerRunning, timeRemaining, chouquetteMode, chouquetteTriggered]);

  // Chouquette debt handler
  const handleChouquetteDebt = useCallback(() => {
    if (!currentMember) return;
    setChouquetteTriggered(true);
    actions.addChouquetteDebt({
      memberId: currentMember.accountId,
      memberName: currentMember.displayName,
      timestamp: new Date(),
    });
  }, [currentMember, actions]);

  // Next member
  const handleNext = useCallback(() => {
    actions.completePacmanTurn();
  }, [actions]);

  // Mark absent
  const handleAbsent = useCallback(() => {
    if (currentMember) {
      actions.markAbsent(currentMember);
      actions.completePacmanTurn();
    }
  }, [currentMember, actions]);

  // Mark late
  const handleLate = useCallback(() => {
    if (currentMember) {
      actions.markLate(currentMember);
      actions.completePacmanTurn();
    }
  }, [currentMember, actions]);

  // Add blocker
  const handleBlocker = useCallback(() => {
    if (!currentMember || !blockerText.trim()) return;

    const blocker: Blocker = {
      memberId: currentMember.accountId,
      memberName: currentMember.displayName,
      description: blockerText.trim(),
      timestamp: new Date(),
    };

    actions.addBlocker(blocker);
    setBlockerText('');
    setShowBlockerInput(false);
  }, [currentMember, blockerText, actions]);

  // Keyboard controls
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (showBlockerInput) {
        if (e.code === 'Escape') {
          setShowBlockerInput(false);
          setBlockerText('');
        } else if (e.code === 'Enter') {
          handleBlocker();
        }
        return;
      }

      switch (e.code) {
        case 'Tab':
          e.preventDefault();
          actions.setPhase('RECAP');
          break;
        case 'Space':
          handleNext();
          break;
        case 'KeyX':
          handleAbsent();
          break;
        case 'KeyY':
          handleLate();
          break;
        case 'KeyB':
          setShowBlockerInput(true);
          setTimeout(() => blockerInputRef.current?.focus(), 100);
          break;
        case 'Escape':
          actions.setPhase('PACMAN_MAZE');
          break;
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [showBlockerInput, handleNext, handleAbsent, handleLate, handleBlocker]);

  // Focus blocker input
  useEffect(() => {
    if (showBlockerInput) {
      blockerInputRef.current?.focus();
    }
  }, [showBlockerInput]);

  // Fireworks easter egg
  useFireworksCode(() => setShowFireworks(true), isLastMember);

  if (!currentMember) return null;

  const issues = currentMember.issues || [];
  const capturePosition = pacmanCaptureOrder.indexOf(currentMember.accountId) + 1;

  return (
    <div className="pacman-kanban-screen">
      {/* Pac-Man styled border */}
      <div className="pacman-kanban-screen__border">
        {/* Header */}
        <header className="pacman-kanban-screen__header">
          <div className="pacman-kanban-screen__member">
            <div
              className="pacman-kanban-screen__avatar"
              style={{ backgroundColor: getAvatarColor(currentMember.displayName) }}
            >
              {getInitials(currentMember.displayName)}
            </div>
            <div className="pacman-kanban-screen__info">
              <h2>{currentMember.displayName}</h2>
              <p>#{capturePosition} captured • {issues.length} tasks</p>
            </div>
          </div>

          <div
            className={`pacman-kanban-screen__timer ${
              timeRemaining <= WARNING_THRESHOLD ? 'pacman-kanban-screen__timer--warning' : ''
            }`}
          >
            <span className="timer-icon">⏱️</span>
            <span className="timer-value">{formatTime(timeRemaining)}</span>
          </div>
        </header>

        {/* Kanban board */}
        <main className="pacman-kanban-screen__main">
          <IssuesKanban issues={issues} />
        </main>

        {/* Actions */}
        <footer className="pacman-kanban-screen__footer">
          {showBlockerInput ? (
            <div className="blocker-input">
              <input
                ref={blockerInputRef}
                type="text"
                value={blockerText}
                onChange={e => setBlockerText(e.target.value)}
                placeholder="Blocker description..."
                className="blocker-input__field"
              />
              <button onClick={handleBlocker} className="blocker-input__submit">
                OK
              </button>
              <span className="blocker-input__hint">[ENTER] Submit [ESC] Cancel</span>
            </div>
          ) : (
            <>
              <div className="pacman-kanban-screen__buttons">
                <button className="action-button action-next" onClick={handleNext}>
                  [SPACE] NEXT
                </button>
                <button className="action-button action-absent" onClick={handleAbsent}>
                  [X] ABSENT
                </button>
                <button className="action-button action-late" onClick={handleLate}>
                  [Y] LATE
                </button>
                <button className="action-button action-blocker" onClick={() => {
                  setShowBlockerInput(true);
                  setTimeout(() => blockerInputRef.current?.focus(), 100);
                }}>
                  [B] BLOCKER
                </button>
              </div>
              <div className="pacman-kanban-screen__hints">
                <span>[TAB] Recap</span>
                <span>[ESC] Back to maze</span>
              </div>
            </>
          )}
        </footer>
      </div>

      {showFireworks && <FireworksOverlay onComplete={() => setShowFireworks(false)} />}
    </div>
  );
}
